import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
} from "@mui/material";
import { getTermsAndConditions } from "../lib/conversation-config";

interface TermsAndConditionsDialogProps {
  open: boolean;
  onAccept: () => void;
  countryCode: string;
}

export const TermsAndConditionsDialog: React.FC<
  TermsAndConditionsDialogProps
> = ({ open, onAccept, countryCode }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get terms and conditions for the country
  const termsConfig = countryCode ? getTermsAndConditions(countryCode) : null;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const scrolledToBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight,
      ) < 5;

    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  useEffect(() => {
    if (!open) return;

    const checkScrollNeeded = () => {
      const element = scrollBoxRef.current;
      if (!element) return;

      const scrollable = element.scrollHeight > element.clientHeight;
      setIsScrollable(scrollable);

      setHasScrolledToBottom(!scrollable);
    };

    const id = window.setTimeout(checkScrollNeeded, 100);
    return () => window.clearTimeout(id);
  }, [open, isMobile]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
          borderRadius: isMobile ? 0 : "16px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          maxHeight: "90vh",
          margin: isMobile ? 0 : "32px",
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: { xs: 1, sm: 1.5 },
          pt: { xs: 1, sm: 1.5 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1.375rem",
              sm: "1.5rem",
            },
          }}
        >
          {termsConfig?.title || "Terms & Conditions"}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: { xs: 0.5, sm: 1 },
          pb: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <div
          ref={scrollBoxRef}
          onScroll={handleScroll}
          style={{
            maxHeight: "55vh",
            overflowY: "auto",
            paddingRight: "8px",
            position: "relative",
          }}
        >
          {termsConfig?.content && (
            <Typography
              variant="body2"
              paragraph
              sx={{
                fontSize: {
                  xs: "0.875rem",
                  sm: "0.9375rem",
                },
                lineHeight: 1.6,
                whiteSpace: "pre-line",
              }}
            >
              {termsConfig.content}
            </Typography>
          )}

          {termsConfig?.sections &&
            termsConfig.sections.length > 0 &&
            termsConfig.sections.map((section: any, index: number) => (
              <React.Fragment key={index}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mt: { xs: 1.5, sm: 2 },
                    mb: 0.5,
                    fontWeight: 600,
                    fontSize: {
                      xs: "0.9375rem",
                      sm: "1rem",
                    },
                  }}
                >
                  {index + 1}. {section.title}
                </Typography>
                <Typography
                  variant="body2"
                  paragraph
                  sx={{
                    fontSize: {
                      xs: "0.875rem",
                      sm: "0.9375rem",
                    },
                    lineHeight: 1.6,
                  }}
                >
                  {section.content}
                </Typography>
              </React.Fragment>
            ))}

          {termsConfig?.footer && (
            <Typography
              variant="body2"
              sx={{
                mt: { xs: 2, sm: 2.5 },
                fontStyle: "italic",
                fontSize: {
                  xs: "0.8125rem",
                  sm: "0.875rem",
                },
                color: "text.secondary",
                lineHeight: 1.5,
              }}
            >
              {typeof termsConfig.footer === "string"
                ? termsConfig.footer
                : (() => {
                    const { text, links } = termsConfig.footer;
                    const parts = text.split(/(\{link\d+\})/g);
                    return parts.map((part: string, index: number) => {
                      const linkMatch = part.match(/\{(link\d+)\}/);
                      if (linkMatch && links[linkMatch[1]]) {
                        const linkConfig = links[linkMatch[1]];
                        return (
                          <Link
                            key={index}
                            href={linkConfig.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "primary.main",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "primary.dark",
                              },
                            }}
                          >
                            {linkConfig.label}
                          </Link>
                        );
                      }
                      return (
                        <React.Fragment key={index}>{part}</React.Fragment>
                      );
                    });
                  })()}
            </Typography>
          )}
        </div>

        {isScrollable && !hasScrolledToBottom && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              mt: { xs: 1, sm: 1.5 },
              color: "primary.main",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: {
                xs: "0.75rem",
                sm: "0.8125rem",
              },
            }}
          >
            ↓ Please scroll to the bottom to enable the Accept button ↓
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          pt: { xs: 1, sm: 1.5 },
          pb: { xs: 1.75, sm: 1.5 },
          px: { xs: 2, sm: 2.5 },
        }}
      >
        <button
          className="action-button"
          onClick={onAccept}
          disabled={!hasScrolledToBottom}
          style={{
            minWidth: isMobile ? "100%" : "160px",
            width: isMobile ? "100%" : "auto",
            padding: isMobile ? "12px 20px" : "10px 20px",
            fontSize: isMobile ? "0.9375rem" : "0.875rem",
            opacity: hasScrolledToBottom ? 1 : 0.5,
            cursor: hasScrolledToBottom ? "pointer" : "not-allowed",
          }}
        >
          {termsConfig?.buttonText || "Accept Terms & Conditions"}
        </button>
      </DialogActions>
    </Dialog>
  );
};
